import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '../../../utils/validation';
import type { IServiceDeclarationActivity } from '../../interfaces/declarationActivity';
import type { IInsurerServiceDeclarationRule } from '../../interfaces/insurerServiceDeclarationRule';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import {
    insurerServiceDeclarationActivityAddSchema,
    insurerServiceDeclarationActivityEditSchema,
} from '../../../../common/validation/insurerServiceDeclarationActivity';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import {
    CancelButton,
    DangerAlertWhenErrorCode,
    FormContainer,
    Header,
    InputContainer,
    InputCurrency,
    Loading,
    SaveSubmit,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { FormContextProvider } from '../../elements/formContext';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { DateField } from '../../fields/Date';
import { DropdownField } from '../../fields/Dropdown';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';

type IInsurerServiceDeclarationActivityAddEditProps = IWithRouterProps;

interface IInsurerServiceDeclarationActivity {
    endDate: string | null;
    id?: string;
    serviceDeclarationActivityId: string;
    startDate: string;
    supplementPriceActivity: number | null;
}

interface IInsurerServiceDeclarationActivityAddEditState {
    errorCode?: string;
    errors: ValidationErrors;
    insurerServiceDeclarationActivity: IInsurerServiceDeclarationActivity;
    insurerServiceDeclarationRule: IInsurerServiceDeclarationRule | null;
    isSaving: boolean;
    serviceDeclarationActivities: IServiceDeclarationActivity[];
}

class InnerInsurerServiceDeclarationActivityAddEdit extends Component<
    IInsurerServiceDeclarationActivityAddEditProps,
    IInsurerServiceDeclarationActivityAddEditState
> {
    constructor(props: IInsurerServiceDeclarationActivityAddEditProps) {
        super(props);

        this.state = {
            errors: emptyValidationErrors,
            insurerServiceDeclarationActivity: {
                endDate: null,
                serviceDeclarationActivityId: '',
                startDate: '',
                supplementPriceActivity: null,
            },
            insurerServiceDeclarationRule: null,
            isSaving: false,
            serviceDeclarationActivities: [],
        };
    }

    public async componentDidMount() {
        if (this.props.router.params.id) {
            this.load();
        } else {
            await this.setState(({ insurerServiceDeclarationActivity }) => ({
                insurerServiceDeclarationActivity: {
                    ...insurerServiceDeclarationActivity,
                    insurerServiceDeclarationRuleId: this.props.router.searchParams.get(
                        'insurerServiceDeclarationRuleId'
                    ),
                },
            }));
        }

        await this.loadInsurerServiceDeclarationRule();
        await this.loadServiceDeclarationActivities();
    }

    public render() {
        return (
            <FixedWidthTemplate>
                <Header
                    content={
                        this.props.router.params.id
                            ? translateKey(
                                  'common.editItem',
                                  translateKey('serviceDeclarationRules.declarationActivity')
                              )
                            : translateKey(
                                  'common.addItem',
                                  translateKey('serviceDeclarationRules.declarationActivity')
                              )
                    }
                />
                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />
                <Loading
                    item={this.state.insurerServiceDeclarationActivity}
                    loaded={(insurerServiceDeclarationActivity) => (
                        <FormContainer onSubmit={async () => await this.save()}>
                            <FormContextProvider
                                value={{
                                    errors: this.state.errors,
                                    item: insurerServiceDeclarationActivity,
                                    onChange: async (key, value) => await this.handleChange(key, value),
                                    onDoValidate: async (key) => await this.validateOne(key),
                                    placeholderGroupName: 'insurers',
                                }}
                            >
                                {this.state.serviceDeclarationActivities && (
                                    <DropdownField
                                        fieldKey="serviceDeclarationActivityId"
                                        isRequired
                                        label={translateKey('serviceDeclarationRules.declarationActivity')}
                                        placeholder={translateKey(
                                            'common.selectItem',
                                            translateKey('serviceDeclarationRules.declarationActivity')
                                        )}
                                        selectOptions={this.state.serviceDeclarationActivities.map((activity) => ({
                                            text: `${activity.codeActiviteit} - ${activity.activityType} `,
                                            value: activity.id,
                                        }))}
                                    />
                                )}

                                <DateField
                                    birthDate
                                    fieldKey="startDate"
                                    isRequired
                                    label={translateKey('common.startDate')}
                                    placeholder={translateKey('common.startDate')}
                                    size="medium"
                                />
                                <DateField
                                    birthDate
                                    fieldKey="endDate"
                                    label={translateKey('common.endDate')}
                                    placeholder={translateKey('common.endDate')}
                                    size="medium"
                                />

                                <HorizontalFormGroup hasErrors={this.state.errors.has('supplementPriceActivity')}>
                                    <FormLabel>{translateKey('insurers.supplementPriceActivity')}</FormLabel>
                                    <InputContainer medium>
                                        <InputCurrency
                                            error={this.state.errors.get('supplementPriceActivity')}
                                            fieldKey="supplementPriceActivity"
                                            value={insurerServiceDeclarationActivity.supplementPriceActivity}
                                        />
                                    </InputContainer>
                                </HorizontalFormGroup>
                            </FormContextProvider>

                            <CancelButton
                                disabled={this.state.isSaving}
                                to={this.getReturnUrl()}
                            />
                            <SaveSubmit disabled={this.state.isSaving} />
                        </FormContainer>
                    )}
                />
            </FixedWidthTemplate>
        );
    }

    private async handleChange<K extends keyof IInsurerServiceDeclarationActivity>(
        name: K,
        value: IInsurerServiceDeclarationActivity[K]
    ) {
        await this.setState(({ insurerServiceDeclarationActivity }) => ({
            insurerServiceDeclarationActivity: { ...insurerServiceDeclarationActivity, [name]: value },
        }));
    }

    private async validateOne(name: keyof IInsurerServiceDeclarationActivity) {
        await this.setState(({ insurerServiceDeclarationActivity, errors }) => ({
            errors: updateValidationErrorsForOne(
                insurerServiceDeclarationActivityEditSchema,
                insurerServiceDeclarationActivity,
                errors,
                name
            ),
        }));
    }

    private async load() {
        const url = `/api/insurerServiceDeclarationActivities/${this.props.router.params.id}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerServiceDeclarationActivity: result.json });
    }

    private async save() {
        let url = '/api/insurerServiceDeclarationActivities/';

        if (this.props.router.params.id) {
            url += this.props.router.params.id;
        }

        const { errors, item } = validate(
            this.props.router.params.id
                ? insurerServiceDeclarationActivityEditSchema
                : insurerServiceDeclarationActivityAddSchema,
            this.state.insurerServiceDeclarationActivity
        );
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        await this.setState({ isSaving: true });

        const result = await fetchJson(url, 'POST', item, [ValidationErrorCode.OVERLAPPING_CONFIG]);
        if (result.errorCode) {
            await this.setState({ errorCode: result.errorCode, isSaving: false });
        } else {
            this.props.router.navigate(this.getReturnUrl());
        }
    }

    private getReturnUrl() {
        const insurerServiceDeclarationRuleId = this.props.router.searchParams.get('insurerServiceDeclarationRuleId');
        const insurerId = this.props.router.searchParams.get('insurerId');
        const serviceId = this.props.router.searchParams.get('serviceId');
        const returnPage = this.props.router.searchParams.get('returnPage');

        const newParams = new URLSearchParams({
            ...(insurerId && { insurerId }),
            ...(serviceId && { serviceId }),
            ...(returnPage && { returnPage }),
        });

        return `/insurerServiceDeclarationRules/${insurerServiceDeclarationRuleId}/edit?${newParams.toString()}`;
    }

    private async loadServiceDeclarationActivities() {
        if (this.state.insurerServiceDeclarationRule) {
            const url = `/api/serviceDeclarationActivities?serviceId=${this.state.insurerServiceDeclarationRule.serviceId}`;
            const result = await fetchJson(url, 'GET');
            await this.setState({ serviceDeclarationActivities: result.json.items });
        }
    }

    private async loadInsurerServiceDeclarationRule() {
        const url = `/api/insurerServiceDeclarationRules/${this.props.router.searchParams.get(
            'insurerServiceDeclarationRuleId'
        )}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ insurerServiceDeclarationRule: result.json });
    }
}

export const InsurerServiceDeclarationActivityAddEdit = withRouter(InnerInsurerServiceDeclarationActivityAddEdit);
